import { useCallback, useEffect, useState } from 'react';

import { useEnrolledTenants } from '@/hooks/use-enrolled-tenants';
import { useSelfQuery } from '@/hooks/use-self';
import * as api from '@/lib/api-client';
import { useLocation, useNavigate } from 'react-router';
import { useTenantCreationRequests } from '@/hooks/use-tenant-creation-requests';
import { MerchantStorePicker } from '@/pages/select-tenant/merchant-store-picker';
import { StaffStorePicker } from '@/pages/select-tenant/staff-store-picker';
import type { StorePickerProps } from '@/pages/select-tenant/store-picker-props';

export default function SelectTenantPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: tenants,
    isLoading: isLoadingTenants,
    isError: isTenantsError,
  } = useEnrolledTenants();
  const { data: user, isLoading: isLoadingUser } = useSelfQuery();
  const [isSingleTenantCheckPending, setIsSingleTenantCheckPending] =
    useState(true);

  const tenantCreationRequests = useTenantCreationRequests();

  const handleSelectTenant = useCallback(
    (tenantSlug: string) => {
      const redirect = location.state?.redirect as Omit<typeof location, 'key'>;
      if (redirect) {
        const redirectPathname = redirect.pathname.replace('_', tenantSlug); // replace the placeholder slug from the original pathname
        navigate(`${redirectPathname}${redirect.search}${redirect.hash}`, {
          state: redirect.state,
        });
      } else {
        navigate(`/${tenantSlug}/dashboard`);
      }
    },
    [location.state?.redirect, navigate]
  );

  // If only one tenant is available, redirect automatically
  useEffect(() => {
    // A failed query never produces data, so without this the check below
    // would wait forever and the page would stay blank with no way out (not
    // even "Sair") — e.g. a stale session whose requests all fail.
    if (isTenantsError || tenantCreationRequests.isError) {
      setIsSingleTenantCheckPending(false);
      return;
    }

    if (!tenants || !tenantCreationRequests.data) {
      return;
    }

    if (location.state?.skipAutoRedirect) {
      setIsSingleTenantCheckPending(false);
      return;
    }

    if (tenants.length === 1 && tenantCreationRequests.data.length === 0) {
      handleSelectTenant(tenants[0].slug);
    } else if (
      tenants.length === 0 &&
      tenantCreationRequests.data.length === 1
    ) {
      navigate(`/onboarding/${tenantCreationRequests.data[0].slug}`);
    } else {
      setIsSingleTenantCheckPending(false);
    }
  }, [
    handleSelectTenant,
    location.state?.skipAutoRedirect,
    tenants,
    navigate,
    tenantCreationRequests.data,
    tenantCreationRequests.isError,
    isTenantsError,
  ]);

  const handleLogout = async () => {
    await api.logout();
    navigate('/login');
  };

  const isLoading =
    isLoadingTenants ||
    isLoadingUser ||
    isSingleTenantCheckPending ||
    tenantCreationRequests.isLoading;

  if (isLoading) {
    return <div></div>;
  }

  const pickerProps: StorePickerProps = {
    tenants: tenants ?? [],
    creationRequests: tenantCreationRequests.data ?? [],
    email: user?.email,
    onSelect: handleSelectTenant,
    onLogout: handleLogout,
  };

  // Korza staff see every store (see TenantAccessService.hasAccess in the
  // backend), so they get a searchable A–Z grid; merchants get their few
  // stores up close.
  return user?.isBackofficeUser ? (
    <StaffStorePicker {...pickerProps} />
  ) : (
    <MerchantStorePicker {...pickerProps} />
  );
}
